/**
 * SigTDD Pricing Page — `/pricing`.
 *
 * Two tiers. Limits come from the generated tier file (server tier table,
 * framing V4); the Pro price is the single constant in `lib/tier.ts`.
 */

import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { track } from '../../lib/analytics';
import { PRO_PRICE_AUD_MONTHLY, TIER_LIMIT } from '../../lib/tier';
import Link from '../Link';
import './PricingPage.css';

// Relative URLs: the Vite dev server proxies /api to the Express API (vite.config.ts); production serves both from one host.
const API_BASE = '';

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

interface PricingPageProps {
  onSignup?: () => void;
}

interface FeatureRow {
  label: string;
  free: string | boolean;
  pro: string | boolean;
}

const free = TIER_LIMIT.free;
const pro = TIER_LIMIT.pro;

const features: FeatureRow[] = [
  { label: 'Saved corridors', free: String(free.maxCorridors), pro: String(pro.maxCorridors) },
  { label: 'Plans per corridor', free: String(free.maxPlansPerCorridor), pro: String(pro.maxPlansPerCorridor) },
  { label: 'Intersections per corridor', free: String(free.maxIntersectionsPerCorridor), pro: String(pro.maxIntersectionsPerCorridor) },
  { label: 'CSV import & export', free: true, pro: true },
  { label: 'Copy diagram to clipboard', free: true, pro: true },
  { label: 'Auto-sequence (NB/SB)', free: true, pro: true },
  { label: 'Auto-sequence Smart mode', free: free.smartAutoSequence, pro: pro.smartAutoSequence },
  { label: 'PDF export', free: free.pdfExport, pro: pro.pdfExport },
  { label: 'Synchro import', free: free.synchroImport, pro: pro.synchroImport },
  { label: 'All display options', free: true, pro: true },
];

export default function PricingPage({ onSignup }: PricingPageProps) {
  const { user } = useAuth();
  const [upgrading, setUpgrading] = useState(false);
  const isPro = user?.tier === 'pro';

  const handleUpgrade = async () => {
    setUpgrading(true);
    track('upgrade-click', { source: 'pricing' });
    try {
      const res = await fetch(`${API_BASE}/api/stripe/create-checkout-session`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch {
      setUpgrading(false);
    }
  };

  const handleManage = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/stripe/create-portal-session`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch { /* no-op */ }
  };

  return (
    <main className="pricing-page">
      <div className="pricing-container">
        <header className="pricing-hero">
          <h1>Pricing</h1>
          <p>Start free. Upgrade when you need more.</p>
        </header>

        <div className="pricing-cards">
          {/* Free Plan */}
          <div className="pricing-card">
            <div className="pricing-card-header">
              <h2>Free</h2>
              <p>For evaluation and small projects</p>
            </div>
            <div className="pricing-card-price">
              <span className="pricing-amount">$0</span>
              <span className="pricing-period">/month</span>
            </div>
            <ul className="pricing-features">
              {features.map((f, i) => (
                <li key={i} className={f.free === false ? 'disabled' : ''}>
                  {f.free === false
                    ? <XIcon />
                    : <CheckIcon />}
                  <span>
                    {typeof f.free === 'string'
                      ? <><strong>{f.free}</strong> {f.label}</>
                      : f.label}
                  </span>
                </li>
              ))}
            </ul>
            {user && !isPro ? (
              <button className="pricing-cta pricing-cta-secondary" disabled>
                Current Plan
              </button>
            ) : !user ? (
              <>
                <button className="pricing-cta pricing-cta-secondary" onClick={onSignup}>
                  Get Started
                </button>
                <Link to="/features" className="pricing-cta pricing-cta-secondary pricing-cta-link">
                  See Features
                </Link>
              </>
            ) : null}
            {!isPro && <p className="pricing-note">No credit card required</p>}
          </div>

          {/* Pro Plan */}
          <div className="pricing-card pricing-card-featured">
            <div className="pricing-badge">{isPro ? 'Your Plan' : 'Recommended'}</div>
            <div className="pricing-card-header">
              <h2>Pro</h2>
              <p>For professional traffic engineers</p>
            </div>
            <div className="pricing-card-price">
              <span className="pricing-amount">${PRO_PRICE_AUD_MONTHLY}</span>
              <span className="pricing-period">/month</span>
            </div>
            <ul className="pricing-features">
              {features.map((f, i) => (
                <li key={i}>
                  <CheckIcon />
                  <span>
                    {typeof f.pro === 'string'
                      ? <><strong>{f.pro}</strong> {f.label}</>
                      : f.label}
                  </span>
                </li>
              ))}
            </ul>
            {isPro ? (
              <button className="pricing-cta pricing-cta-primary" onClick={handleManage}>
                Manage Subscription
              </button>
            ) : user ? (
              <button className="pricing-cta pricing-cta-primary" onClick={handleUpgrade} disabled={upgrading}>
                {upgrading ? 'Redirecting...' : 'Upgrade to Pro'}
              </button>
            ) : (
              <>
                <button className="pricing-cta pricing-cta-primary" onClick={onSignup}>
                  Start Free, Upgrade Anytime
                </button>
                <Link to="/features" className="pricing-cta pricing-cta-secondary pricing-cta-link">
                  See Features
                </Link>
              </>
            )}
            <p className="pricing-note">{isPro ? 'Manage or cancel anytime' : 'Prices in Australian dollars. Cancel anytime.'}</p>
          </div>
        </div>

        <section className="pricing-detail">
          <h2>What the limits mean</h2>
          <p>
            A <strong>corridor</strong> is one set of intersections with their spacing and speeds. A{' '}
            <strong>timing plan</strong> is one set of offsets and phase durations for that corridor, so the
            free tier's {free.maxPlansPerCorridor} plans per corridor cover, for example, an AM and a PM
            peak. The <strong>intersection</strong> limit is per corridor. Import, auto-sequence, display
            options, share links and clipboard copy work the same on both tiers; PDF export and Synchro UTDF
            import are Pro features because they carry the most processing.
          </p>
          <p>
            Pro is billed monthly through Stripe and can be cancelled from the subscription portal at any
            time; the account then returns to the free limits at the end of the paid period.
          </p>
          <p>
            <Link to="/app">Open the tool</Link> · <Link to="/docs" event="docs-open" source="pricing">Documentation</Link> · <Link to="/contact">Contact</Link>
          </p>
        </section>
      </div>
    </main>
  );
}
